import { DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { AssetInstance } from '../../core/models/asset-instance.model';
import { CheckPoint } from '../../core/models/checkPoint.model';
import { Form } from '../../core/models/form.model';
import { InteractionRecord } from '../../core/models/interaction.model';
import { AssetInstanceService } from '../../core/services/asset-instance.service';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { CheckPointService } from '../../core/services/checkPoint.service';
import { FormDefinitionService } from '../../core/services/form-definition.service';
import { FormService } from '../../core/services/form.service';
import { FormSumbissionService } from '../../core/services/formSubmission.service';
import { LoggerService } from '../../core/services/logger.service';
import { TemplateMetadataService } from '../../core/services/template-metadata.service';
import { TranslationService } from '../../core/services/translation.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { CheckpointFormHeaderComponent } from '../../shared/components/checkpoint-form-header/checkpoint-form-header.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

/** Field types that are already read-only and need no transformation. */
const PASSTHROUGH_TYPES = new Set(['text-label', 'qr-code']);

@Component({
  selector: 'app-view-submission',
  imports: [
    DatePipe,
    ReactiveFormsModule,
    FormlyModule,
    TranslatePipe,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CheckpointFormHeaderComponent,
    SpinnerComponent,
    ButtonComponent,
    IconComponent,
  ],
  templateUrl: './view-submission.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './view-submission.component.css',
})
export class ViewSubmissionComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private checkPointService = inject(CheckPointService);
  private assetInstanceService = inject(AssetInstanceService);
  private formService = inject(FormService);
  private submissionService = inject(FormSumbissionService);
  private formDefinitionService = inject(FormDefinitionService);
  private logger = inject(LoggerService).getLogger('viewSubmission');
  private translate = inject(TranslationService);
  private breadcrumbService = inject(BreadcrumbService);
  private templateMetadataService = inject(TemplateMetadataService);

  formlyGroup = new FormGroup({});
  formlyOptions: FormlyFormOptions = {};
  model: Record<string, any> = {};
  fields: FormlyFieldConfig[] = [];

  loading = signal(true);
  error = signal<string | null>(null);

  checkPoint = signal<CheckPoint | null>(null);
  form = signal<Form | null>(null);
  instance = signal<AssetInstance | null>(null);
  record = signal<InteractionRecord | null>(null);
  formColumns = signal<1 | 2 | 3 | 4>(1);

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  async loadData(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const checkpointId = this.route.snapshot.paramMap.get('checkpointId');
      const formId = this.route.snapshot.paramMap.get('formId');
      const recordId = this.route.snapshot.paramMap.get('recordId');
      const instanceId = this.route.snapshot.paramMap.get('instanceId');

      if (!checkpointId || !formId || !recordId) {
        this.error.set(this.translate.translate('submission.form.error.missingParams'));
        this.loading.set(false);
        return;
      }

      // Load data in parallel where possible
      const [checkPoint, form, submission] = await Promise.all([
        this.checkPointService.getById(checkpointId),
        this.formService.getById(formId),
        this.submissionService.getById(recordId),
      ]);

      if (!checkPoint) {
        this.error.set(
          this.translate.translate('submission.form.error.checkpointNotFound', {
            checkpointId,
          }),
        );
        this.loading.set(false);
        return;
      }
      this.checkPoint.set(checkPoint);

      if (!form) {
        this.error.set(this.translate.translate('submission.form.error.formNotFound', { formId }));
        this.loading.set(false);
        return;
      }
      this.form.set(form);

      if (!submission) {
        this.error.set(this.translate.translate('submission.view.error.notFound'));
        this.loading.set(false);
        return;
      }
      this.record.set(submission);

      // Load instance if specified
      if (instanceId) {
        const instance = await this.assetInstanceService.getById(instanceId);
        if (instance) {
          this.instance.set(instance);
        }
      }

      this.formlyOptions.formState = {
        templateContext: this.templateMetadataService.buildContext(checkPoint, this.instance()),
      };

      // Load form definition and transform fields
      if (form.form_definition_id) {
        const formDef = await this.formDefinitionService.getFormDefinitionFull(
          form.form_definition_id,
        );
        if (formDef) {
          this.fields = this.transformFieldsToReadonly(formDef.fields);
          this.formColumns.set(formDef.columns);
        }
      }

      // Set model from submission data
      this.model = submission.form_data ?? {};

      // Set breadcrumbs
      const breadcrumbLabels = this.instance()
        ? [
            checkPoint.name,
            form.name,
            `#${this.instance()!.serial_number}`,
            this.translate.translate('submission.view.title'),
          ]
        : [checkPoint.name, form.name, this.translate.translate('submission.view.title')];
      this.breadcrumbService.setDynamicLabels(breadcrumbLabels);

      this.loading.set(false);
    } catch (err) {
      this.logger.error('Failed to load submission:', err);
      this.error.set(this.translate.translate('submission.view.error.loadFailed'));
      this.loading.set(false);
    }
  }

  goBack(): void {
    const checkpointId = this.route.snapshot.paramMap.get('checkpointId');
    const formId = this.route.snapshot.paramMap.get('formId');
    const instanceId = this.route.snapshot.paramMap.get('instanceId');

    const segments = instanceId
      ? ['/submission', checkpointId, formId, instanceId, 'history']
      : ['/submission', checkpointId, formId, 'history'];
    this.router.navigate(segments);
  }

  get submittedAt(): string | null {
    return this.record()?.created_at ?? null;
  }

  get recordedBy(): string | null {
    const data = this.record()?.form_data;
    if (!data) return null;
    return (data['_recorded_by'] as string) ?? null;
  }

  private transformFieldsToReadonly(fields: FormlyFieldConfig[]): FormlyFieldConfig[] {
    return fields.map((field) => {
      const originalType = field.type as string;

      if (PASSTHROUGH_TYPES.has(originalType)) {
        return field;
      }

      if (originalType === 'photo') {
        return {
          ...field,
          type: 'readonly-photo',
        };
      }

      return {
        ...field,
        type: 'readonly-field',
        props: {
          ...field.props,
          originalType,
        },
      };
    });
  }
}
